import { useMemo } from "react";
import styled from "styled-components";
import { Theme } from "../../../../../../constant/Theme";
import type { InrixVolumesViewOrderItemSummary } from "../../../../../../types/ViewOrderItem/InrixVolumes/InrixVolumesViewOrderItemSummary";
import { useThemeContext } from "../../../../../theme/ThemeContext";
import {
  InrixVolumeTimePeriod,
  useGetOrderItemInrixVolumeParams,
} from "../controls";
import {
  calculateSqrtScaledStrokeWidth,
  DEFAULT_STROKE_WIDTH,
  INRIX_VOLUME_LINE_COLOR,
} from "../utils/inrixVolumeLineStyle";

const KeyCard = styled.div<{ $isDark: boolean }>`
  min-height: 48px;
  background-color: ${({ $isDark }) => ($isDark ? "#2b2b2b" : "#ffffff ")};
`;

const KeyRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LineSample = styled.span<{ $width: number }>`
  display: inline-block;
  width: 25px;
  height: ${({ $width }) => `${Math.max(1, $width)}px`};
  background-color: ${INRIX_VOLUME_LINE_COLOR};
  border-radius: 999px;
`;

interface OrderItemInrixVolumeLineThicknessKeyProps {
  summary: InrixVolumesViewOrderItemSummary | null;
}

const OrderItemInrixVolumeLineThicknessKey = ({
  summary,
}: OrderItemInrixVolumeLineThicknessKeyProps) => {
  const { timePeriod } = useGetOrderItemInrixVolumeParams();
  const { theme } = useThemeContext();

  const { minValue, maxValue } = useMemo(() => {
    if (timePeriod === InrixVolumeTimePeriod.ADT) {
      return { minValue: summary?.adtMin, maxValue: summary?.adtMax };
    }

    return { minValue: summary?.volMin, maxValue: summary?.volMax };
  }, [
    summary?.adtMax,
    summary?.adtMin,
    summary?.volMax,
    summary?.volMin,
    timePeriod,
  ]);

  const maxStrokeWidth = calculateSqrtScaledStrokeWidth(
    maxValue ?? null,
    minValue,
    maxValue,
  );
  const minStrokeWidth = calculateSqrtScaledStrokeWidth(
    minValue ?? null,
    minValue,
    maxValue,
  );

  return (
    <KeyCard
      $isDark={theme === Theme.DARK}
      className={`rounded px-3 py-2 ${theme === Theme.DARK ? "text-white" : "text-black"}`}
    >
      <KeyRow>
        <LineSample $width={maxStrokeWidth} />
        <span>{`Max (${Math.round(maxValue ?? 0) ?? "-"})`}</span>
        <LineSample $width={summary ? minStrokeWidth : DEFAULT_STROKE_WIDTH} />
        <span>{`Min (${Math.round(minValue ?? 0) ?? "-"})`}</span>
      </KeyRow>
    </KeyCard>
  );
};

export default OrderItemInrixVolumeLineThicknessKey;
